import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../context/ThemeContext';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { Menu, Sun, Moon, X, User, ChevronDown, Users, Handshake, CalendarDays, Info, Building2 } from 'lucide-react';
import LoginModal from './LoginModal';
import LanguageSelector from './LanguageSelector';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_dd86d20d-7215-40c9-ab96-b556c05959d7/artifacts/n0vf802x_Plan%20de%20travail%2040.png";

export default function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginModalTab, setLoginModalTab] = useState('staff');
  const [showAssociationMenu, setShowAssociationMenu] = useState(false);
  const [showMobileAssociationMenu, setShowMobileAssociationMenu] = useState(false);
  const [showEventsMenu, setShowEventsMenu] = useState(false);
  const [showMobileEventsMenu, setShowMobileEventsMenu] = useState(false);
  
  // Events submenu items
  const eventsSubLinks = [
    { path: '/evenements', label: t('nav.events'), icon: CalendarDays },
    { path: '/infos-pratiques', label: 'Infos Pratiques', icon: Info },
  ];
  
  // Association submenu items
  const associationSubLinks = [
    { path: '/association', label: 'Le Bureau', icon: Building2 },
    { path: '/benevoles', label: t('nav.volunteer'), icon: Users },
    { path: '/partenaires', label: t('nav.partners'), icon: Handshake },
  ];
  
  // Secret admin access: 5 clicks on logo
  const logoClicksRef = useRef(0);
  const logoTimerRef = useRef(null);
  const associationMenuRef = useRef(null);
  const eventsMenuRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (associationMenuRef.current && !associationMenuRef.current.contains(event.target)) {
        setShowAssociationMenu(false);
      }
      if (eventsMenuRef.current && !eventsMenuRef.current.contains(event.target)) {
        setShowEventsMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogoClick = (e) => {
    logoClicksRef.current += 1;
    
    if (logoTimerRef.current) {
      clearTimeout(logoTimerRef.current);
    }
    
    if (logoClicksRef.current >= 5) {
      e.preventDefault();
      logoClicksRef.current = 0;
      setLoginModalTab('staff');
      setShowLoginModal(true);
    } else {
      logoTimerRef.current = setTimeout(() => {
        logoClicksRef.current = 0;
      }, 2000);
    }
  };

  const user = localStorage.getItem('briouso_user') ? JSON.parse(localStorage.getItem('briouso_user')) : null;

  const isActive = (path) => location.pathname === path;
  const isAssociationActive = associationSubLinks.some(link => location.pathname === link.path);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled
          ? 'glass-nav shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <nav className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="logo-hover flex items-center gap-3" data-testid="logo-link" onClick={handleLogoClick}>
            <img
              src={LOGO_URL}
              alt="Brious'O Folies"
              className="h-14 w-auto"
            />
            <span className="hidden md:block font-['Playfair_Display'] text-xl font-bold">
              Brious'O Folies
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6">
            {/* Accueil */}
            <Link
              to="/"
              data-testid="nav-link-home"
              className={`text-sm font-medium uppercase tracking-widest transition-colors hover:text-fuchsia-500 ${
                isActive('/') ? 'text-fuchsia-500 nav-link-active' : ''
              }`}
            >
              {t('nav.home')}
            </Link>

            {/* Événements with dropdown */}
            <div className="relative" ref={eventsMenuRef} onMouseLeave={() => setShowEventsMenu(false)}>
              <Link
                to="/evenements"
                onMouseEnter={() => setShowEventsMenu(true)}
                className={`flex items-center gap-1 text-sm font-medium uppercase tracking-widest transition-colors hover:text-fuchsia-500 ${
                  isActive('/evenements') || isActive('/infos-pratiques') ? 'text-fuchsia-500 nav-link-active' : ''
                }`}
                data-testid="nav-link-evenements"
              >
                {t('nav.events')}
                <ChevronDown className={`h-4 w-4 transition-transform ${showEventsMenu ? 'rotate-180' : ''}`} />
              </Link>

              {showEventsMenu && (
                <div 
                  className="absolute top-full left-0 pt-2 z-50"
                  onMouseEnter={() => setShowEventsMenu(true)}
                >
                  <div className="w-52 bg-white rounded-xl shadow-lg border border-zinc-200 py-2 animate-in fade-in slide-in-from-top-2">
                    {eventsSubLinks.map((link) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        onClick={() => setShowEventsMenu(false)}
                        className={`flex items-center gap-3 px-4 py-2 text-sm hover:bg-zinc-50 transition-colors ${
                          isActive(link.path) ? 'bg-fuchsia-50 text-fuchsia-700' : 'text-zinc-700'
                        }`}
                      >
                        {link.icon && <link.icon className="h-4 w-4" />}
                        {link.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Association with dropdown */}
            <div className="relative" ref={associationMenuRef} onMouseLeave={() => setShowAssociationMenu(false)}>
              <Link
                to="/association"
                onMouseEnter={() => setShowAssociationMenu(true)}
                className={`flex items-center gap-1 text-sm font-medium uppercase tracking-widest transition-colors hover:text-fuchsia-500 ${
                  isAssociationActive ? 'text-fuchsia-500 nav-link-active' : ''
                }`}
                data-testid="nav-association-link"
              >
                {t('nav.association')}
                <ChevronDown className={`h-4 w-4 transition-transform ${showAssociationMenu ? 'rotate-180' : ''}`} />
              </Link>

              {/* Dropdown menu - pt-2 as invisible bridge instead of mt-2 gap */}
              {showAssociationMenu && (
                <div 
                  className="absolute top-full left-0 pt-2 z-50"
                  onMouseEnter={() => setShowAssociationMenu(true)}
                >
                  <div className="w-48 bg-white rounded-xl shadow-lg border border-zinc-200 py-2 animate-in fade-in slide-in-from-top-2">
                    {associationSubLinks.map((link) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        onClick={() => setShowAssociationMenu(false)}
                        className={`flex items-center gap-3 px-4 py-2 text-sm hover:bg-zinc-50 transition-colors ${
                          isActive(link.path) ? 'bg-fuchsia-50 text-fuchsia-700' : 'text-zinc-700'
                        }`}
                      >
                        {link.icon && <link.icon className="h-4 w-4" />}
                        {link.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Venir */}
            <Link
              to="/venir"
              data-testid="nav-link-venir"
              className={`text-sm font-medium uppercase tracking-widest transition-colors hover:text-fuchsia-500 ${
                isActive('/venir') ? 'text-fuchsia-500 nav-link-active' : ''
              }`}
            >
              {t('nav.gettingHere')}
            </Link>

            {/* Actualités */}
            <Link
              to="/actualites"
              data-testid="nav-link-actualites"
              className={`text-sm font-medium uppercase tracking-widest transition-colors hover:text-fuchsia-500 ${
                isActive('/actualites') ? 'text-fuchsia-500 nav-link-active' : ''
              }`}
            >
              {t('nav.news')}
            </Link>

            {/* Contact */}
            <Link
              to="/contact"
              data-testid="nav-link-contact"
              className={`text-sm font-medium uppercase tracking-widest transition-colors hover:text-fuchsia-500 ${
                isActive('/contact') ? 'text-fuchsia-500 nav-link-active' : ''
              }`}
            >
              {t('nav.contact')}
            </Link>
          </div>

          {/* Theme Toggle & Mobile Menu */}
          <div className="flex items-center gap-2 md:gap-4">
            {/* Language Selector */}
            <LanguageSelector />
            
            {/* User button */}
            {user ? (
              <Link to={user.role === 'admin' || user.role === 'collaborateur' ? '/admin' : '/portail-adherent'}>
                <Button
                  variant="ghost"
                  size="icon"
                  className="rounded-full bg-fuchsia-100"
                  data-testid="user-btn"
                >
                  <User className="h-5 w-5 text-fuchsia-600" />
                </Button>
              </Link>
            ) : (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  setLoginModalTab('adherent');
                  setShowLoginModal(true);
                }}
                className="rounded-full"
                data-testid="adherent-login-btn"
              >
                <User className="h-5 w-5" />
              </Button>
            )}
            
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              data-testid="theme-toggle"
              className="rounded-full"
            >
              {theme === 'dark' ? (
                <Sun className="h-5 w-5" />
              ) : (
                <Moon className="h-5 w-5" />
              )}
            </Button>

            {/* Mobile Menu */}
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon" data-testid="mobile-menu-trigger">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-80 bg-background">
                <div className="flex flex-col h-full pt-8">
                  <div className="flex items-center justify-between mb-8">
                    <img src={LOGO_URL} alt="Brious'O Folies" className="h-12 w-auto" />
                  </div>
                  <nav className="flex flex-col gap-2">
                    {/* Accueil */}
                    <Link
                      to="/"
                      onClick={() => setIsOpen(false)}
                      className={`text-lg font-medium py-2 border-b border-border transition-colors hover:text-fuchsia-500 ${
                        isActive('/') ? 'text-fuchsia-500' : ''
                      }`}
                    >
                      {t('nav.home')}
                    </Link>

                    {/* Événements with mobile submenu */}
                    <div className="border-b border-border">
                      <div className="flex items-center justify-between">
                        <Link
                          to="/evenements"
                          onClick={() => setIsOpen(false)}
                          className={`flex-1 text-lg font-medium py-2 transition-colors hover:text-fuchsia-500 ${
                            isActive('/evenements') ? 'text-fuchsia-500' : ''
                          }`}
                        >
                          {t('nav.events')}
                        </Link>
                        <button
                          onClick={() => setShowMobileEventsMenu(!showMobileEventsMenu)}
                          className="p-2 hover:text-fuchsia-500 transition-colors"
                        >
                          <ChevronDown className={`h-5 w-5 transition-transform ${showMobileEventsMenu ? 'rotate-180' : ''}`} />
                        </button>
                      </div>
                      {showMobileEventsMenu && (
                        <div className="pl-4 pb-2 space-y-1">
                          {eventsSubLinks.map((link) => (
                            <Link
                              key={link.path}
                              to={link.path}
                              onClick={() => setIsOpen(false)}
                              className={`flex items-center gap-2 py-1.5 text-sm transition-colors hover:text-fuchsia-500 ${
                                isActive(link.path) ? 'text-fuchsia-500' : 'text-zinc-600'
                              }`}
                            >
                              {link.icon && <link.icon className="h-4 w-4" />}
                              {link.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Association with mobile submenu */}
                    <div className="border-b border-border">
                      <div className="flex items-center justify-between">
                        <Link
                          to="/association"
                          onClick={() => setIsOpen(false)}
                          className={`flex-1 text-lg font-medium py-2 transition-colors hover:text-fuchsia-500 ${
                            isActive('/association') ? 'text-fuchsia-500' : ''
                          }`}
                        >
                          {t('nav.association')}
                        </Link>
                        <button
                          onClick={() => setShowMobileAssociationMenu(!showMobileAssociationMenu)}
                          className="p-2 hover:text-fuchsia-500 transition-colors"
                        >
                          <ChevronDown className={`h-5 w-5 transition-transform ${showMobileAssociationMenu ? 'rotate-180' : ''}`} />
                        </button>
                      </div>
                      
                      {showMobileAssociationMenu && (
                        <div className="pl-4 pb-2 space-y-1">
                          {associationSubLinks.map((link) => (
                            <Link
                              key={link.path}
                              to={link.path}
                              onClick={() => setIsOpen(false)}
                              className={`flex items-center gap-2 py-2 text-base transition-colors hover:text-fuchsia-500 ${
                                isActive(link.path) ? 'text-fuchsia-500' : 'text-zinc-600'
                              }`}
                            >
                              {link.icon && <link.icon className="h-4 w-4" />}
                              {link.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Venir */}
                    <Link
                      to="/venir"
                      onClick={() => setIsOpen(false)}
                      className={`text-lg font-medium py-2 border-b border-border transition-colors hover:text-fuchsia-500 ${
                        isActive('/venir') ? 'text-fuchsia-500' : ''
                      }`}
                    >
                      {t('nav.gettingHere')}
                    </Link>

                    {/* Actualités */}
                    <Link
                      to="/actualites"
                      onClick={() => setIsOpen(false)}
                      className={`text-lg font-medium py-2 border-b border-border transition-colors hover:text-fuchsia-500 ${
                        isActive('/actualites') ? 'text-fuchsia-500' : ''
                      }`}
                    >
                      {t('nav.news')}
                    </Link>

                    {/* Contact */}
                    <Link
                      to="/contact"
                      onClick={() => setIsOpen(false)}
                      className={`text-lg font-medium py-2 border-b border-border transition-colors hover:text-fuchsia-500 ${
                        isActive('/contact') ? 'text-fuchsia-500' : ''
                      }`}
                    >
                      {t('nav.contact')}
                    </Link>
                  </nav>
                  <div className="mt-auto pb-8">
                    {user ? (
                      <Link
                        to={user.role === 'admin' || user.role === 'collaborateur' ? '/admin' : '/portail-adherent'}
                        onClick={() => setIsOpen(false)}
                        className="btn-primary block text-center"
                        data-testid="mobile-portal-btn"
                      >
                        Mon Espace
                      </Link>
                    ) : (
                      <button
                        onClick={() => {
                          setIsOpen(false);
                          setLoginModalTab('adherent');
                          setShowLoginModal(true);
                        }}
                        className="btn-primary block w-full text-center"
                        data-testid="mobile-login-btn"
                      >
                        Espace Adhérent
                      </button>
                    )}
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </nav>
      </div>
      
      {/* Login Modal */}
      <LoginModal 
        isOpen={showLoginModal} 
        onClose={() => setShowLoginModal(false)} 
        defaultTab={loginModalTab}
      />
    </header>
  );
}
